import { type JSX, memo } from "react";
import styled from "styled-components";
import { lookupAiTranslation } from "../../aiTranslationUtils";
import { currentColors } from "../../colors";
import t from "../../translations";
import type { ImageSelectionAnswerType } from "../../types";
import ImagePinIcon from "../icons/ImagePinIcon";
import SelectInImage from "./SelectInImage";

interface IProps {
    image: {
        id: string;
        mediaType: string;
        src: string;
    };
    imageSelectionAnswers: {
        id: string;
        part: number;
        clickX: number;
        clickY: number;
        answerType: ImageSelectionAnswerType | "undecided";
        answer: string | null;
        explanation: string | null;
    }[];
    imageSelectionScore: {
        earnedScore: number;
        possibleScore: number;
    };
    translations?: Record<string, string>;
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const AnswersList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const AnswerItem = styled.div`
    display: grid;
    grid-template-columns: auto auto 1fr;
    gap: 0.75rem;
    align-items: start;
`;

const PinContainer = styled.div`
    width: 30px;
    height: 60px;
    flex-shrink: 0;
`;

const StyledPin = styled(ImagePinIcon)`
    width: 100%;
    height: 100%;
`;

const AnswerContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const ScoreColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ScoreValue = styled.div`
    font-weight: bold;
    font-size: 1.1rem;
`;

const ScoreLabel = styled.div`
    font-size: 0.75rem;
    color: ${currentColors.grey4};
    text-align: center;
`;

const AnswerContainer = styled.div`
    display: block;
`;

const AnswerLabel = styled.span`
    font-weight: bold;
`;

const AnswerText = styled.span`
    font-weight: normal;
`;

const ExplanationContainer = styled.div`
    display: block;
`;

const ExplanationLabel = styled.span`
    font-weight: bold;
`;

const ExplanationText = styled.span`
    font-weight: normal;
`;

const ScoreText = styled.p`
    margin: 0;
    font-weight: bold;
`;

function getPinColor(answerType: ImageSelectionAnswerType | "undecided"): string {
    switch (answerType) {
        case "correct":
            return currentColors.selectInImageCorrect;
        case "incorrect":
            return currentColors.selectInImageIncorrect;
        case "missed":
            return currentColors.selectInImage;
        default:
            return currentColors.selectInImage;
    }
}

function getScoreContribution(answerType: ImageSelectionAnswerType | "undecided"): number {
    switch (answerType) {
        case "correct":
            return 2;
        case "incorrect":
            return -1;
        case "undecided":
            return 0;
        case "missed":
            return 0;
        default:
            return 0;
    }
}

function SelectInImageResult({ image, imageSelectionAnswers, imageSelectionScore, translations }: IProps): JSX.Element {
    return (
        <Container>
            {imageSelectionScore.possibleScore > 0 && (
                <ScoreText>
                    {t("shared.select-in-image-result.score", {
                        earned_score: imageSelectionScore.earnedScore,
                        possible_score: imageSelectionScore.possibleScore,
                    })}
                </ScoreText>
            )}
            <SelectInImage image={image} pins={imageSelectionAnswers} />

            <AnswersList>
                {imageSelectionAnswers.map((answer) => {
                    const pinColor = getPinColor(answer.answerType);
                    const scoreContribution = getScoreContribution(answer.answerType);

                    return (
                        <AnswerItem key={answer.id}>
                            <ScoreColumn>
                                <ScoreValue>
                                    {scoreContribution > 0 ? `+${scoreContribution}` : scoreContribution}
                                </ScoreValue>
                                <ScoreLabel>{t("shared.select-in-image-result.score-points")}</ScoreLabel>
                            </ScoreColumn>

                            <PinContainer>
                                <StyledPin number={answer.part} color={pinColor} />
                            </PinContainer>

                            <AnswerContent>
                                {(answer.answerType === "correct" || answer.answerType === "incorrect") && (
                                    <>
                                        <AnswerContainer>
                                            <AnswerLabel>
                                                {t("shared.select-in-image-result.answer")}
                                                {": "}
                                            </AnswerLabel>
                                            <AnswerText>{lookupAiTranslation(answer.answer, translations)}</AnswerText>
                                        </AnswerContainer>
                                        {answer.explanation && (
                                            <ExplanationContainer>
                                                <ExplanationLabel>
                                                    {t("shared.select-in-image-result.explanation")}
                                                    {": "}
                                                </ExplanationLabel>
                                                <ExplanationText>
                                                    {lookupAiTranslation(answer.explanation, translations)}
                                                </ExplanationText>
                                            </ExplanationContainer>
                                        )}
                                    </>
                                )}
                                {answer.answerType === "undecided" && (
                                    <AnswerContainer>
                                        <AnswerLabel>
                                            {t("shared.select-in-image-result.answer")}
                                            {": "}
                                        </AnswerLabel>
                                        <AnswerText>{lookupAiTranslation(answer.answer, translations)}</AnswerText>
                                    </AnswerContainer>
                                )}

                                {answer.answerType === "missed" && (
                                    <>
                                        <div>{t("shared.select-in-image-result.missed-area")}</div>
                                        {answer.explanation && (
                                            <ExplanationContainer>
                                                <ExplanationLabel>
                                                    {t("shared.select-in-image-result.explanation")}
                                                    {": "}
                                                </ExplanationLabel>
                                                <ExplanationText>
                                                    {lookupAiTranslation(answer.explanation, translations)}
                                                </ExplanationText>
                                            </ExplanationContainer>
                                        )}
                                    </>
                                )}
                            </AnswerContent>
                        </AnswerItem>
                    );
                })}
            </AnswersList>
        </Container>
    );
}

export default memo(SelectInImageResult);
